import { useTranslations } from "@zapier/i18n";
import { useCallback } from "react";

import {
  Button,
  Link,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import {
  App,
  AppWithImplementationsMeta,
  Connection,
  useHydrateAppsWithImplementationsMeta,
  useQueryPrivateApps,
  useApp,
  useRefetchAuthentications,
} from "@zapier/client";

import { getZapsByConnectionUrl, useNotification } from "@zapier/utils";
import styles from "./ConnectionsReplaceModal.module.css";
import { ConnectionPreviewCard } from "./ConnectionPreviewCard";
import useCreateAppConnection from "../useCreateAppConnection";

interface Props {
  id?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  connection: Connection;
  connectionAppId?: number;
}

interface ContentProps extends Props {
  onSubmit: () => void;
  isLoading: boolean;
}

function getMemberCount(connection: Connection): number | undefined {
  const groupTotal = (connection?.groups ?? []).reduce(
    (sum, group) => sum + (group.memberCount ?? 0),
    0,
  );
  const memberTotal = connection?.members?.length ?? 0;
  const total = groupTotal + memberTotal;
  return total > 0 ? total : undefined;
}

function ConnectionsReplaceContent({
  connection,
  connectionAppId,
  isLoading,
  onSubmit,
  setIsOpen,
}: ContentProps) {
  const t = useTranslations("connections-replace-modal");
  const zapCount = connection?.zapCount ?? 0;
  const memberCount = getMemberCount(connection);

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onSubmit();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            event.preventDefault();
            return event.key === "Enter" && onSubmit();
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("header.replace-connection-title")}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          <div className={styles.body}>
            <div className={styles.section}>
              <Text type="Body3" fontWeight={600}>
                {t("labels.preview-intro")}
              </Text>
              <ConnectionPreviewCard connection={connection} />
            </div>

            <div className={styles.section}>
              <Text type="Body3" fontWeight={600}>
                {t("labels.what-this-means")}
              </Text>
              <ul className={styles.bulletList}>
                <li>
                  {t.rich("bullets.zaps-impact", {
                    zapCount,
                    strong: (chunks) => <Text fontWeight={600}>{chunks}</Text>,
                  })}
                </li>
                {memberCount !== undefined && (
                  <li>
                    {t.rich("bullets.members-impact", {
                      memberCount,
                      strong: (chunks) => (
                        <Text fontWeight={600}>{chunks}</Text>
                      ),
                    })}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button type="submit" isDisabled={!connectionAppId || isLoading}>
            {t("buttons.continue")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export default function ConnectionsReplace({
  id,
  isOpen,
  setIsOpen,
  connection,
}: Props) {
  const t = useTranslations("connections-replace-modal");
  const { refetchAuthentications } = useRefetchAuthentications();
  const { createNotification } = useNotification();
  const createNewConnection = useCreateAppConnection();

  const { app: queryApp, isLoading: isUseQueryAppLoading } = useApp({
    slug: connection?.implementation?.slug || "",
    selectedApi: connection?.selectedApi || "",
  });

  const { apps: privateApps, isLoading: isUseQueryPrivateAppsLoading } =
    useQueryPrivateApps(
      {
        limit: 1,
        selectedApis: [connection?.selectedApi || ""],
      },
      {
        enabled: !!connection?.selectedApi && !connection?.implementation?.slug,
      },
    );

  const app = queryApp || privateApps?.[0];

  const { apps: hydratedApps, isLoading: isHydratedAppsLoading } =
    useHydrateAppsWithImplementationsMeta({
      apps: [app as App],
      onlyAppsWithAuth: false,
    });

  const isLoading =
    isUseQueryAppLoading ||
    isUseQueryPrivateAppsLoading ||
    isHydratedAppsLoading;

  const connectionApp = hydratedApps[0];

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const replaceConnection = useCallback(async () => {
    if (!id || !connectionApp?.implementationMeta?.authType) {
      return;
    }

    // Replace the connection - this is equivalent to creating a new connection
    const { id: newConnectionId, app } = await createNewConnection(
      connectionApp as AppWithImplementationsMeta,
      connection,
    );
    if (newConnectionId) {
      const newConnectionUrl = getZapsByConnectionUrl(
        app?.currentImplementationId || "",
        app?.slug || "",
        newConnectionId as number,
      );
      createNotification({
        message: (
          <Text type="Body1">
            {t.rich("notifications.connection-replaced-success", {
              connectionTitle: connection?.title || "",
              connectionTitleText: (chunks) => (
                <Text type="Body3">{chunks}</Text>
              ),
              reviewZapsLink: (chunks) => (
                <Link href={newConnectionUrl}>{chunks}</Link>
              ),
            })}
          </Text>
        ),
        type: "success",
      });
      refetchAuthentications();
    }

    setIsOpen(false);
  }, [
    connection,
    connectionApp,
    createNewConnection,
    createNotification,
    id,
    refetchAuthentications,
    setIsOpen,
    t,
  ]);

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <ConnectionsReplaceContent
      connection={connection}
      id={id}
      isLoading={isLoading}
      isOpen={isOpen}
      onSubmit={replaceConnection}
      setIsOpen={setIsOpen}
      connectionAppId={
        connectionApp?.implementationMeta?.authType
          ? connectionApp.id
          : undefined
      }
    />
  );
}
